import React, { Suspense } from "react";
import { Route, Switch, useLocation } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import {
  selectSidebarMiniUserSet,
  setSidebarMini,
} from "store/reducers/uiReducer";

import Box from "@mui/material/Box";

// core components
// Our own logo, bundled rather than loaded from Builder.io's CDN.
// PNG, not WebP: Amplify's SPA rewrite has an extension allowlist that does
// not include webp, so the hosted .webp URL returned index.html and the
// wordmark rendered as broken alt text on every page. Same pixels.
// Brand refresh R5 (2026-09-04): the rev-2 wordmark — Hanken Grotesk outlines,
// one ink, teal tittles on all four i's. SVG outlines, so no font dependency
// and no Amplify rewrite-allowlist concern (svg is on the allowlist).
import optimicitiWordmark from "assets/img/brand/optimiciti_wordmark_refresh.svg";
import Navbar from "components/common/Navbar.js";
import Sidebar from "components/common/Sidebar.js";
import Footer from "components/common/Footer.js";
import SkipToContent from "components/common/SkipToContent.jsx";
import SolveAnnouncer from "components/common/SolveAnnouncer.jsx";
import NotFound from "components/domain/NotFound.js";

import SolvePoller from "components/common/SolvePoller.jsx";

import routes from "routes.js";


const Models = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const sidebarMiniUserSet = useSelector(selectSidebarMiniUserSet);

  const isFirstRoute = React.useRef(true);
  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    // SPA route changes swap content without a page load, so screen-reader and
    // keyboard users are left focused on the old page's link. Move focus to the
    // new page's <main> (skipped on first mount so initial focus stays natural).
    if (isFirstRoute.current) {
      isFirstRoute.current = false;
    } else {
      document.getElementById("main-content")?.focus({ preventScroll: true });
    }
    // Depend on the PATHNAME, not the whole location — see the matching note in
    // Admin.js. A query-string change is not a new page, and treating it as one
    // steals focus from any input that mirrors its state into the URL.
  }, [location.pathname]);

  React.useEffect(() => {
    // Models/examples pages should default to expanded sidebar.
    // Only apply defaults if the user hasn't explicitly chosen a preference this session.
    if (sidebarMiniUserSet) {
      return;
    }

    dispatch(setSidebarMini(false));
  }, [dispatch, sidebarMiniUserSet]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.collapse) {
        return getRoutes(prop.views);
      }
      if (prop.layout === "/models") {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      }
      else if (prop.layout === "/examples") {
        return (
          <Route
            path={prop.layout + prop.path}
            component={prop.component}
            key={key}
          />
        );
      }
      
      else {
        return null;
      }
    });
  };
  return (
    <>
      {/* Isolated leaf: hosts the async-solve poller's subscription so its
          frequent updates don't re-render this layout (and the map). */}
      <SolvePoller />
      <SolveAnnouncer />
      <SkipToContent />
      <Box display="flex" id='Models-Box1' height='100vh'>
        {/* Desktop only — it hides itself below the navbar's mobile breakpoint,
            which matters most here: the map is the point of these pages and a
            250px drawer left about a third of a phone screen for it. */}
        <Sidebar
          routes={routes}
          logo={{
            innerLink: "/home",
            imgSrc: optimicitiWordmark,
            imgAlt: "Optimiciti Logo",
          }}
        />
        <Box
          id='Models-Box2'
          position="relative"
          flex="1"
          sx={{
            minHeight: '100vh',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
            transition: 'all 0.33s cubic-bezier(0.685, 0.0473, 0.346, 1)',
            width: { xs: '100%', md: 'auto' },
          }}
          style={{
            minWidth:'0',
            overflowX:'hidden',
            overflowY:'auto',
            height:'100%',
            display:'flex',
            flexDirection:'column',
            backgroundColor: '#f4f6f8'
          }}
        >
          <Navbar></Navbar>
          {/* The main landmark: skip-link target + route-change focus target.
              tabIndex={-1} makes it programmatically focusable without joining
              the tab order; the outline is suppressed since this focus is a
              navigation aid, not an interactive control. */}
          {/* overflow:visible is load-bearing, not cosmetic: argon.min.css carries a
              bare `main { overflow: hidden }` reset rule. It lay dormant while this
              wrapper was a <div>; making it a <main> landmark activated it, which
              clipped the content area so the page could not grow or scroll and the
              footer read as stuck. This restores the pre-landmark behavior (a plain
              div computes overflow:visible) and the sx class outranks the element
              selector, so it cannot be silently re-clipped. */}
          <Box component="main" id="main-content" tabIndex={-1} flex="1" sx={{ outline: "none", overflow: "visible" }}>
            {/* The optimizer, model builder, examples and account routes are code
                split (see routes.js), so rendering them needs a boundary. The
                fallback is deliberately empty rather than a spinner: these
                chunks resolve in milliseconds on a warm cache, and a flashed
                spinner reads as slower than nothing at all. */}
            <Suspense fallback={null}>
            <Switch>
              {getRoutes(routes)}
              <Route component={NotFound} />
            </Switch>
            </Suspense>
          </Box>
          <Footer />
        </Box>
      </Box>
    </>
  );
};

export default Models;
